import React from 'react';

function ServiceDetail({ service, onBack }) {
  // Detailed content mapping for each service category
  const serviceData = {
    'Digital Marketing': {
      title: 'Digital Marketing Services',
      icon: '📈',
      tagline: 'We help businesses improve their online visibility and connect with their target audience through effective digital marketing strategies.',
      longDesc: 'Digital marketing is the engine of modern business growth. At Saveera Biotech, we don\'t focus on vanity metrics like likes or page impressions. Instead, we design data-driven campaigns engineered to generate qualified inbound leads, convert prospects, and increase overall revenue.',
      subSections: [
        {
          title: 'Search Engine Optimization (SEO)',
          points: [
            'In-depth technical SEO audits checking crawl budgets, indexing errors, and page load latency.',
            'Keyword research targeting low-competition, high-intent keywords to secure page-one rankings.',
            'On-page content optimization aligning metadata, H1/H2 header hierarchies, and image alt text.',
            'Sustainable link-building campaigns to improve domain authority and search visibility.'
          ]
        },
        {
          title: 'Social Media & Brand Promotion',
          points: [
            'Design of high-quality custom graphics, typography assets, and template grids.',
            'Organic content calendar strategy focusing on LinkedIn, Instagram, and Facebook branding.',
            'Targeted paid acquisition ad campaigns on Meta, Google, and LinkedIn with pixel tracking.',
            'Audience growth plans, influencer outreach, and community engagement moderation.'
          ]
        },
        {
          title: 'Lead Generation & Analytics',
          points: [
            'Creation of conversion-optimized landing pages with clear call-to-actions (CTAs).',
            'Lead magnets (white papers, custom tools, audit spreadsheets) to capture business emails.',
            'Looker Studio dashboards providing real-time data on campaign spend, clicks, and conversion rates.',
            'Google Tag Manager event monitoring, heatmaps, and weekly analytics reviews.'
          ]
        }
      ]
    },
    'IT Consultancy': {
      title: 'IT Consultancy Services',
      icon: '🧑‍💼',
      tagline: 'Our IT consultancy services help businesses understand and implement technology according to their requirements.',
      longDesc: 'Technology should serve your business workflows, not complicate them. Many companies waste capital on software subscriptions they don\'t use or build complex stacks that are hard to maintain. Our consultants audit your existing setups and deliver practical, cost-effective technology roadmaps.',
      subSections: [
        {
          title: 'IT Strategy & Code Audits',
          points: [
            'Auditing redundant software platforms and subscriptions to reduce operating overhead.',
            'Security audits analyzing network setups, server file permissions, and database encryptions.',
            'Legacy code reviews to identify refactoring needs, slow SQL queries, and API bugs.',
            'Scalability roadmaps planning cloud resources and server allocations for upcoming growth.'
          ]
        },
        {
          title: 'Technology Stack Advisory',
          points: [
            'Aligning your software project needs with the correct languages (React, Node, Python, Go).',
            'Database evaluation selecting SQL (PostgreSQL, MySQL) vs NoSQL (MongoDB, DynamoDB) systems.',
            'Recommending hosting models (AWS Serverless, GCP, Docker clusters) matching your budget.',
            'Ensuring technology choices prevent vendor lock-in and secure long-term developer availability.'
          ]
        },
        {
          title: 'Digital Transformation & Automation',
          points: [
            'Phased planning to migrate legacy spreadsheet systems and local data files to secure cloud databases.',
            'Workflow automations integrating CRM databases with invoicing, email templates, and SMS alerts.',
            'Custom middleware creation connecting disjointed third-party APIs together.',
            'Developing employee training documentation and design guidelines to ensure tool adoption.'
          ]
        }
      ]
    },
    'Web & Digital Solutions': {
      title: 'Web & Digital Solutions',
      icon: '🌐',
      tagline: 'We provide digital solutions that help businesses establish a professional and effective online presence.',
      longDesc: 'Your website is the digital front door of your enterprise. We engineer custom React applications and digital portals that combine blazing-fast load speeds, premium aesthetics, and responsive layout structures to maximize client trust.',
      subSections: [
        {
          title: 'Corporate Website Solutions',
          points: [
            'Custom single-page and multi-page corporate portals built on React and optimized for performance.',
            'Mobile-first responsive grids ensuring pixel-perfect display on phones, tablets, and desktops.',
            'SEO-friendly semantic HTML5 layouts with schema markup to secure search engine rich snippets.',
            'SSL security certificates, custom domain routing, and fast global CDN hosting.'
          ]
        },
        {
          title: 'Custom Portals & Dashboards',
          points: [
            'Internal admin dashboards to visualize lead data, sales pipelines, and operations metrics.',
            'Secure client portals allowing partners to upload documents, track project tasks, and pay invoices.',
            'Inventory control tools, scheduler calendars, and workflow monitoring portals.',
            'Interactive charts and graphs built using modern visualization libraries.'
          ]
        },
        {
          title: 'E-Commerce Integrations',
          points: [
            'Digital storefront engineering with secure shopping carts, user logins, and order histories.',
            'Stripe, Razorpay, and PayPal gateway setups ensuring PCI-compliant checkout processes.',
            'Webhook setups syncing order data with inventory management and courier shipping APIs.',
            'Promo code setups, review systems, and custom product search catalogs.'
          ]
        }
      ]
    },
    'Computer & IT Services': {
      title: 'Computer & IT Services',
      icon: '💻',
      tagline: 'Our registered business activity includes computer programming, consultancy and other information technology and computer services.',
      longDesc: 'High-quality software relies on reliable source code, secure database structures, and automated deployment pipelines. We offer specialized programming services to design, build, audit, and deploy clean tech products.',
      subSections: [
        {
          title: 'Custom Software Engineering',
          points: [
            'Writing clean, maintainable codebases utilizing modern React, Javascript, and Node.js standards.',
            'Creating modular RESTful and GraphQL API servers mapping your business data objects.',
            'Designing secure, normalized relational databases and optimized storage tables.',
            'Debugging complex script syntax errors and resolving performance bugs in existing apps.'
          ]
        },
        {
          title: 'DevOps & Cloud Deployments',
          points: [
            'Containerizing software setups using Docker to ensure consistency across local and server setups.',
            'Creating automated CI/CD deployment pipelines (GitHub Actions, GitLab CI).',
            'Deploying and scaling web products on AWS (S3, EC2, Lambda), GCP, or cloud environments.',
            'Configuring automated daily database backups, server access logs, and error monitors.'
          ]
        },
        {
          title: 'Software Audits & Maintenance',
          points: [
            'Writing automated testing suites (Jest, Playwright) to prevent future regression errors.',
            'Auditing dependencies to update outdated packages and close security vulnerabilities.',
            'Optimizing server response latency by refactoring backend middleware and caching data.',
            'Long-term technical support agreements keeping your custom software secure and online.'
          ]
        }
      ]
    }
  };

  const data = serviceData[service] || serviceData['Digital Marketing'];

  return (
    <div className="service-post-detail" style={{ maxWidth: '850px', margin: '0 auto', padding: '2rem 1.5rem' }}>
      {/* Navigation Breadcrumbs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2.5rem' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            fontWeight: 700,
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <span style={{ color: 'var(--accent-color)', fontSize: '1.2rem' }}>←</span> Back to Services
        </button>
        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          Services / {service}
        </span>
      </div>

      {/* Header Info */}
      <header style={{ marginBottom: '3rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '2.5rem' }}>
        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginBottom: '1.2rem' }}>
          <span style={{ fontSize: '2.2rem' }}>{data.icon}</span>
          <span style={{ color: 'var(--text-primary)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', background: 'rgba(255, 102, 0, 0.08)', padding: '0.3rem 0.8rem', borderRadius: '4px' }}>
            Core Capability
          </span>
        </div>

        <h1 style={{ fontFamily: 'var(--font-title)', fontSize: '3rem', fontWeight: 900, textTransform: 'uppercase', lineHeight: '1.2', color: 'var(--text-primary)', marginBottom: '1.5rem' }}>
          {data.title}
        </h1>

        <p style={{ fontSize: '1.2rem', color: 'var(--accent-color)', fontWeight: 500, lineHeight: '1.6', margin: 0 }}>
          {data.tagline}
        </p>
      </header>

      {/* Main Copy */}
      <article className="blog-article-content">
        <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-primary)', marginBottom: '2.5rem', textAlign: 'justify' }}>
          {data.longDesc}
        </p>

        {data.subSections.map((sub, index) => (
          <div key={index} style={{ marginBottom: '2.5rem' }}>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.5rem', fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-primary)', marginBottom: '1rem', letterSpacing: '0.5px' }}>
              {sub.title}
            </h3>

            <ul className="price-features" style={{ margin: '1rem 0 2rem 0', gap: '1.1rem' }}>
              {sub.points.map((pt, pIdx) => (
                <li key={pIdx} className="price-feature-item" style={{ alignItems: 'flex-start' }}>
                  <span className="check-icon" style={{ marginTop: '2px' }}>✓</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '1.02rem', lineHeight: '1.6' }}>{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </article>

      {/* Footer Nav */}
      <footer style={{ marginTop: '4rem', paddingTop: '2.5rem', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-primary)',
            fontWeight: 700,
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            padding: 0
          }}
        >
          <span style={{ color: 'var(--accent-color)' }}>←</span> Back to Services
        </button>

        <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Saveera Biotech
        </span>
      </footer>
    </div>
  );
}

export default ServiceDetail;
